// Otto's face follows what he says (Wes, Sept 29 2026: "I didn't necessarily see every little emotion ... map that
// out based off of responses"). The stage mood (agenda.stage_moods) is the base; a cue phrase in Otto's own words
// swaps in the matching mood for MOOD_CUE_HOLD_MS, then the stage mood returns. Runs in the browser on the caption
// text: no model call, no delay. The newest cue in the text wins.
import type { Mood } from "./avatarMotion";

export const MOOD_CUE_HOLD_MS = 4000;

/** Lower case; matched on word boundaries. English and Spanish (the consult's two languages). */
export const MOOD_CUES: Readonly<Record<Exclude<Mood, "neutral">, readonly string[]>> = Object.freeze({
  sympathetic: Object.freeze([
    "sorry to hear",
    "i'm sorry",
    "that's tough",
    "that sounds tough",
    "that sounds hard",
    "that's hard",
    "that's rough",
    "frustrating",
    "i hear you",
    "that must be",
    "lo siento",
    "qué difícil",
    "que dificil",
  ]),
  delighted: Object.freeze([
    "love that",
    "love it",
    "that's great",
    "that's awesome",
    "awesome",
    "fantastic",
    "amazing",
    "excellent",
    "congratulations",
    "congrats",
    "me encanta",
    "excelente",
    "genial",
  ]),
  surprised: Object.freeze(["wow", "whoa", "no way", "really?", "impressive", "increíble", "increible"]),
  proud: Object.freeze(["well done", "nice work", "good for you", "proud of", "bien hecho"]),
  warm: Object.freeze(["thank you", "thanks", "welcome", "glad", "appreciate", "happy to", "gracias", "bienvenido"]),
});

function escape(text: string): string {
  return text.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

const PATTERNS: ReadonlyArray<{ mood: Mood; re: RegExp }> = Object.entries(MOOD_CUES).flatMap(([mood, phrases]) =>
  phrases.map((phrase) => ({
    mood: mood as Mood,
    // A letter must not touch either end, so "glad" never matches inside "gladiator".
    re: new RegExp(`(?<![\\p{L}])${escape(phrase)}(?![\\p{L}])`, "giu"),
  })),
);

/** The mood of the newest cue phrase in `text`, or null when there is none. */
export function moodCue(text: string): Mood | null {
  const lower = text.toLowerCase().replace(/[‘’]/g, "'");
  let best: { mood: Mood; at: number } | null = null;
  for (const { mood, re } of PATTERNS) {
    re.lastIndex = 0;
    for (let m = re.exec(lower); m; m = re.exec(lower)) {
      const end = m.index + m[0].length;
      if (!best || end > best.at) best = { mood, at: end };
    }
  }
  return best?.mood ?? null;
}
